import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { 
  ArrowLeft,
  Camera,
  Upload,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Target,
  Trophy,
  Zap,
  Calendar,
  DollarSign,
  Clock,
  Award,
  Star,
  LogOut,
  Shield,
  Bell,
  Eye,
  Edit,
  CheckCircle,
  TrendingUp,
  Flame,
  Users,
  Share2,
  AlertTriangle,
  Plus,
  CreditCard,
  Briefcase,
  UserCheck,
  Timer,
  AlertCircle,
  X
} from 'lucide-react';
import { achievementSystem, type Achievement } from '../utils/achievementSystem';

interface ProfileSettingsProps {
  onBack: () => void;
  onLogout: () => void;
}

const getAchievementIcon = (iconName: string) => {
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    'trophy': Trophy,
    'star': Star,
    'camera': Camera,
    'shield': Shield,
    'sunrise': Zap,
    'graduation-cap': Award,
    'scales': Award,
    'target': Target,
    'calendar': Calendar,
    'clock': Clock,
    'medal': Award,
    'badge': Award
  };
  return iconMap[iconName] || Trophy;
};

export function ProfileSettings({ onBack, onLogout }: ProfileSettingsProps) {
  const [profilePhoto, setProfilePhoto] = useState<string | null>(
    localStorage.getItem('clockit-user-avatar') || null
  );

  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Basic profile data - some from onboarding
  const [profile, setProfile] = useState({
    name: localStorage.getItem('clockit-user-name') || 'Alex Johnson',
    email: '',
    phone: '',
    jobTitle: '',
    workplace: '',
    employeeId: '',
    department: '',
    
    // Wage & Pay Information
    hourlyWage: 0,
    payFrequency: '', // weekly, bi-weekly, monthly, etc.
    overtimeRate: 1.5, // 1.5x is standard
    payPeriodStart: '', // when pay period starts (Monday, Sunday, etc.)
    hasVariableHours: false,
    minimumHours: 0,
    maximumHours: 40,
    
    // Schedule & Work Details
    defaultStartTime: '09:00',
    defaultEndTime: '17:00',
    breakDuration: 30, // minutes
    lunchDuration: 60, // minutes
    workDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    
    // Emergency Contact
    emergencyName: '',
    emergencyPhone: '',
    emergencyRelationship: '',
    
    // Goals & Preferences
    weeklyGoal: 40,
    overtimeGoal: 50,
    wageGoal: 0, // target hourly wage
  });

  // Profile completion tracking
  const getCompletionStatus = () => {
    const sections = [
      {
        name: 'Basic Info',
        fields: ['name', 'email', 'phone'],
        weight: 15,
        icon: User
      },
      {
        name: 'Work Details',
        fields: ['jobTitle', 'workplace', 'employeeId'],
        weight: 20,
        icon: Briefcase
      },
      {
        name: 'Wage & Pay',
        fields: ['hourlyWage', 'payFrequency'],
        weight: 30,
        icon: DollarSign
      },
      {
        name: 'Schedule',
        fields: ['defaultStartTime', 'defaultEndTime'],
        weight: 20,
        icon: Clock
      },
      {
        name: 'Emergency Contact',
        fields: ['emergencyName', 'emergencyPhone'],
        weight: 15,
        icon: UserCheck
      }
    ];

    let totalCompleted = 0;
    const sectionStatus = sections.map(section => {
      const completed = section.fields.filter(field => {
        const value = profile[field as keyof typeof profile];
        return value && value !== '' && value !== 0;
      }).length;
      
      const percentage = (completed / section.fields.length) * 100;
      const weightedScore = (percentage / 100) * section.weight;
      totalCompleted += weightedScore;
      
      return {
        ...section,
        completed,
        total: section.fields.length,
        percentage,
        isComplete: percentage === 100
      };
    });

    return {
      overall: Math.round(totalCompleted),
      sections: sectionStatus
    };
  };

  const completionStatus = getCompletionStatus();

  // Get real achievements data
  const earnedAchievements = achievementSystem.getEarnedAchievements();
  const nextAchievements = achievementSystem.getNextAchievements(3);
  const allAchievements = [...earnedAchievements, ...nextAchievements].slice(0, 6);
  const totalPoints = achievementSystem.getTotalPoints();

  const weeklyStats = {
    hoursWorked: 38.5,
    goalHours: profile.weeklyGoal,
    currentStreak: 12,
    totalEarnings: profile.hourlyWage > 0 ? 38.5 * profile.hourlyWage : 577.50,
    photosCaptured: 24,
    perfectDays: 5
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (limit to 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Image must be smaller than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setProfilePhoto(result);
        // Persist to localStorage immediately
        localStorage.setItem('clockit-user-avatar', result);
        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event('avatarChanged'));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    localStorage.removeItem('clockit-user-avatar');
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('avatarChanged'));
  };



  const handleLogout = () => {
    // Clear local storage and navigate to onboarding
    localStorage.removeItem('clockit-onboarded');
    localStorage.removeItem('clockit-theme');
    localStorage.removeItem('clockit-profile');
    onLogout();
  };

  const toggleSection = (sectionName: string) => {
    if (activeSection === sectionName) {
      setActiveSection(null);
    } else {
      setActiveSection(sectionName);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="app-header sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Profile</h1>
            <p className="text-sm text-muted-foreground">
              {completionStatus.overall}% complete • Unlock full protection
            </p>
          </div>

        </div>
      </div>

      <div className="px-4 space-y-6 pb-8">
        {/* Profile Completion Alert */}
        {completionStatus.overall < 100 && (
          <Alert className="border-warning bg-warning/5">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium text-warning">Complete Your Profile for Full Protection</p>
                <p className="text-sm">
                  Adding wage and work details unlocks automatic overtime tracking, 
                  accurate earnings calculations, and stronger dispute assistance.
                </p>
                <div className="w-full bg-warning/20 rounded-full h-2 mt-2">
                  <div 
                    className="bg-warning h-2 rounded-full transition-all duration-300"
                    style={{ width: `${completionStatus.overall}%` }}
                  />
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Profile Photo & Basic Info */}
        <Card className="clock-it-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="relative">
                <Avatar className="w-20 h-20 ring-2 ring-primary/20">
                  <AvatarImage src={profilePhoto || undefined} />
                  <AvatarFallback className="text-xl bg-primary text-white">
                    {profile.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <div className="flex-1">
                <div>
                  <h3 className="text-xl font-semibold">{profile.name}</h3>
                  <p className="text-muted-foreground">{profile.jobTitle || 'Job title not set'}</p>
                  <p className="text-sm text-muted-foreground">{profile.workplace || 'Workplace not set'}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary" className="text-xs">
                      <Flame className="w-3 h-3 mr-1" />
                      {weeklyStats.currentStreak} day streak
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      <Star className="w-3 h-3 mr-1" />
                      {earnedAchievements.length} achievements ({totalPoints} pts)
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Photo Management */}
        <Card className="clock-it-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Profile Photo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16 ring-2 ring-primary/20">
                  <AvatarImage src={profilePhoto || undefined} />
                  <AvatarFallback className="bg-primary text-white">
                    {profile.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="font-medium">
                    {profilePhoto ? 'Custom Photo Set' : 'Using Default Avatar'}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {profilePhoto 
                      ? 'Your custom photo appears in the app header and profile'
                      : 'Add a custom photo to personalize your profile'
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Upload className="w-4 h-4" />
                    {profilePhoto ? 'Change' : 'Upload'}
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </label>
                {profilePhoto && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleRemovePhoto}
                    className="gap-2 text-destructive hover:text-destructive"
                  >
                    <X className="w-4 h-4" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
            {!profilePhoto && (
              <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Camera className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Add a profile photo</p>
                    <p>Upload a clear, professional photo that represents you. It will appear throughout the app and help personalize your Clock-It experience.</p>
                    <p className="mt-2 text-xs">Supported formats: JPG, PNG, GIF • Max size: 2MB</p>
                    <div className="mt-3 flex items-center gap-2">
                      <label className="cursor-pointer">
                        <Button variant="default" size="sm" className="gap-2">
                          <Upload className="w-4 h-4" />
                          Choose Photo
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handlePhotoUpload}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profile Completion Sections */}
        <Card className="clock-it-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Profile Completion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {completionStatus.sections.map((section) => {
                const IconComponent = section.icon;
                const isActive = activeSection === section.name;
                
                return (
                  <div key={section.name}>
                    <button
                      onClick={() => toggleSection(section.name)}
                      className="w-full text-left p-4 rounded-lg border hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            section.isComplete 
                              ? 'bg-success text-white' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {section.isComplete ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <IconComponent className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{section.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {section.completed}/{section.total} fields complete
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={section.isComplete ? "default" : "outline"}>
                            {Math.round(section.percentage)}%
                          </Badge>
                          {!section.isComplete && (
                            <AlertCircle className="w-4 h-4 text-warning" />
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expandable section content */}
                    {isActive && (
                      <div className="mt-4 p-4 bg-muted/30 rounded-lg space-y-4">
                        {/* Basic Info Fields */}
                        {section.name === 'Basic Info' && (
                          <>
                            <div>
                              <Label htmlFor="name">Full Name</Label>
                              <Input
                                id="name"
                                value={profile.name}
                                onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                              />
                            </div>
                            <div>
                              <Label htmlFor="email">Email Address</Label>
                              <Input
                                id="email"
                                type="email"
                                value={profile.email}
                                onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="your.email@example.com"
                              />
                            </div>
                            <div>
                              <Label htmlFor="phone">Phone Number</Label>
                              <Input
                                id="phone"
                                type="tel"
                                value={profile.phone}
                                onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="+1 (555) 123-4567"
                              />
                            </div>
                          </>
                        )}

                        {/* Work Details Fields */}
                        {section.name === 'Work Details' && (
                          <>
                            <div>
                              <Label htmlFor="jobTitle">Job Title</Label>
                              <Input
                                id="jobTitle"
                                value={profile.jobTitle}
                                onChange={(e) => setProfile(prev => ({ ...prev, jobTitle: e.target.value }))}
                                placeholder="e.g., Server, Cashier, Sales Associate"
                              />
                            </div>
                            <div>
                              <Label htmlFor="workplace">Workplace/Company</Label>
                              <Input
                                id="workplace"
                                value={profile.workplace}
                                onChange={(e) => setProfile(prev => ({ ...prev, workplace: e.target.value }))}
                                placeholder="e.g., Main Street Restaurant"
                              />
                            </div>
                            <div>
                              <Label htmlFor="employeeId">Employee ID (optional)</Label>
                              <Input
                                id="employeeId"
                                value={profile.employeeId}
                                onChange={(e) => setProfile(prev => ({ ...prev, employeeId: e.target.value }))}
                                placeholder="Your employee ID number"
                              />
                            </div>
                            <div>
                              <Label htmlFor="department">Department (optional)</Label>
                              <Input
                                id="department"
                                value={profile.department}
                                onChange={(e) => setProfile(prev => ({ ...prev, department: e.target.value }))}
                                placeholder="e.g., Kitchen, Front of House"
                              />
                            </div>
                          </>
                        )}

                        {/* Wage & Pay Fields */}
                        {section.name === 'Wage & Pay' && (
                          <>
                            <Alert className="border-primary bg-primary/5">
                              <DollarSign className="h-4 w-4 text-primary" />
                              <AlertDescription className="text-primary">
                                <strong>Why this matters:</strong> Accurate wage tracking helps detect underpayment, 
                                calculates overtime, and provides precise earnings data for disputes.
                              </AlertDescription>
                            </Alert>
                            
                            <div>
                              <Label htmlFor="hourlyWage">Hourly Wage ($)</Label>
                              <Input
                                id="hourlyWage"
                                type="number"
                                step="0.01"
                                min="0"
                                value={profile.hourlyWage || ''}
                                onChange={(e) => setProfile(prev => ({ ...prev, hourlyWage: parseFloat(e.target.value) || 0 }))}
                                placeholder="15.50"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Your regular hourly pay rate (before overtime)
                              </p>
                            </div>
                            
                            <div>
                              <Label htmlFor="payFrequency">Pay Frequency</Label>
                              <Select 
                                value={profile.payFrequency} 
                                onValueChange={(value) => setProfile(prev => ({ ...prev, payFrequency: value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="How often are you paid?" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="weekly">Weekly (every week)</SelectItem>
                                  <SelectItem value="bi-weekly">Bi-weekly (every 2 weeks)</SelectItem>
                                  <SelectItem value="semi-monthly">Semi-monthly (twice a month)</SelectItem>
                                  <SelectItem value="monthly">Monthly</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label htmlFor="overtimeRate">Overtime Rate (multiplier)</Label>
                              <Input
                                id="overtimeRate"
                                type="number"
                                step="0.1"
                                min="1"
                                value={profile.overtimeRate}
                                onChange={(e) => setProfile(prev => ({ ...prev, overtimeRate: parseFloat(e.target.value) || 1.5 }))}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Most workers get 1.5x pay for overtime (over 40 hours/week)
                              </p>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div>
                                <Label>Variable Hours</Label>
                                <p className="text-sm text-muted-foreground">Do your hours change weekly?</p>
                              </div>
                              <Switch 
                                checked={profile.hasVariableHours} 
                                onCheckedChange={(checked) => setProfile(prev => ({ ...prev, hasVariableHours: checked }))}
                              />
                            </div>
                          </>
                        )}

                        {/* Schedule Fields */}
                        {section.name === 'Schedule' && (
                          <>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="defaultStartTime">Typical Start Time</Label>
                                <Input
                                  id="defaultStartTime"
                                  type="time"
                                  value={profile.defaultStartTime}
                                  onChange={(e) => setProfile(prev => ({ ...prev, defaultStartTime: e.target.value }))}
                                />
                              </div>
                              <div>
                                <Label htmlFor="defaultEndTime">Typical End Time</Label>
                                <Input
                                  id="defaultEndTime"
                                  type="time"
                                  value={profile.defaultEndTime}
                                  onChange={(e) => setProfile(prev => ({ ...prev, defaultEndTime: e.target.value }))}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="breakDuration">Break Duration (minutes)</Label>
                                <Input
                                  id="breakDuration"
                                  type="number"
                                  min="0"
                                  value={profile.breakDuration}
                                  onChange={(e) => setProfile(prev => ({ ...prev, breakDuration: parseInt(e.target.value) || 0 }))}
                                />
                              </div>
                              <div>
                                <Label htmlFor="lunchDuration">Lunch Duration (minutes)</Label>
                                <Input
                                  id="lunchDuration"
                                  type="number"
                                  min="0"
                                  value={profile.lunchDuration}
                                  onChange={(e) => setProfile(prev => ({ ...prev, lunchDuration: parseInt(e.target.value) || 0 }))}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {/* Emergency Contact Fields */}
                        {section.name === 'Emergency Contact' && (
                          <>
                            <Alert className="border-orange-200 bg-orange-50">
                              <UserCheck className="h-4 w-4 text-orange-600" />
                              <AlertDescription className="text-orange-800">
                                <strong>For your safety:</strong> This contact can be notified if you need 
                                assistance with workplace disputes or emergencies.
                              </AlertDescription>
                            </Alert>
                            
                            <div>
                              <Label htmlFor="emergencyName">Emergency Contact Name</Label>
                              <Input
                                id="emergencyName"
                                value={profile.emergencyName}
                                onChange={(e) => setProfile(prev => ({ ...prev, emergencyName: e.target.value }))}
                                placeholder="Full name of trusted contact"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="emergencyPhone">Emergency Contact Phone</Label>
                              <Input
                                id="emergencyPhone"
                                type="tel"
                                value={profile.emergencyPhone}
                                onChange={(e) => setProfile(prev => ({ ...prev, emergencyPhone: e.target.value }))}
                                placeholder="+1 (555) 123-4567"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="emergencyRelationship">Relationship</Label>
                              <Select 
                                value={profile.emergencyRelationship} 
                                onValueChange={(value) => setProfile(prev => ({ ...prev, emergencyRelationship: value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="How are they related to you?" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="parent">Parent</SelectItem>
                                  <SelectItem value="spouse">Spouse/Partner</SelectItem>
                                  <SelectItem value="sibling">Sibling</SelectItem>
                                  <SelectItem value="friend">Friend</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Weekly Progress - Show earnings if wage is set */}
        {profile.hourlyWage > 0 && (
          <Card className="clock-it-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                This Week's Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gradient-green rounded-xl text-white">
                  <div className="text-2xl font-bold">{weeklyStats.hoursWorked}h</div>
                  <div className="text-sm opacity-90">Hours Worked</div>
                  <div className="text-xs opacity-75">Goal: {weeklyStats.goalHours}h</div>
                </div>
                
                <div className="text-center p-4 bg-gradient-blue rounded-xl text-white">
                  <div className="text-2xl font-bold">${(weeklyStats.hoursWorked * profile.hourlyWage).toFixed(0)}</div>
                  <div className="text-sm opacity-90">Estimated Earnings</div>
                  <div className="text-xs opacity-75">${profile.hourlyWage}/hr</div>
                </div>
                
                <div className="text-center p-4 bg-gradient-purple rounded-xl text-white">
                  <div className="text-2xl font-bold">{weeklyStats.photosCaptured}</div>
                  <div className="text-sm opacity-90">Photos Captured</div>
                  <div className="text-xs opacity-75">Proof Level: Strong</div>
                </div>
                
                <div className="text-center p-4 bg-gradient-orange rounded-xl text-white">
                  <div className="text-2xl font-bold">{weeklyStats.perfectDays}</div>
                  <div className="text-sm opacity-90">Perfect Days</div>
                  <div className="text-xs opacity-75">All shifts documented</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Achievements */}
        <Card className="clock-it-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Achievements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {allAchievements.slice(0, 3).map((achievement) => {
                const IconComponent = getAchievementIcon(achievement.icon);
                return (
                  <div 
                    key={achievement.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border ${
                      achievement.earned 
                        ? 'bg-success/5 border-success/20' 
                        : 'bg-muted/30 border-border'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      achievement.earned 
                        ? 'bg-success text-white' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        {achievement.title}
                        {achievement.earned && <CheckCircle className="w-4 h-4 text-success" />}
                      </div>
                      <div className="text-sm text-muted-foreground">{achievement.description}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        +{achievement.points} points
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Privacy & Notifications */}
        <Card className="clock-it-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Privacy & Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Blur Earnings by Default</Label>
                  <p className="text-sm text-muted-foreground">Hide sensitive information in screenshots</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Work Reminders</Label>
                  <p className="text-sm text-muted-foreground">Get notified before shifts</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Profile Completion Reminders</Label>
                  <p className="text-sm text-muted-foreground">Gentle reminders to complete your profile</p>
                </div>
                <Switch defaultChecked={completionStatus.overall < 100} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logout Section */}
        <Card className="clock-it-card">
          <CardContent className="p-6">
            <Alert className="mb-4">
              <Shield className="w-4 h-4" />
              <AlertDescription>
                Logging out will remove your session but keep all your data safe. 
                You can sign back in anytime with your credentials.
              </AlertDescription>
            </Alert>
            
            <Button 
              variant="destructive" 
              className="w-full h-12"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </CardContent>
        </Card>

        {/* App Info */}
        <div className="text-center text-sm text-muted-foreground py-4">
          <p>Clock-It v1.0.0</p>
          <p>Made with ❤️ for workers everywhere</p>
        </div>
      </div>
    </div>
  );
}